import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { z } from "zod";

export const shiftTimeDefinitionSchema = z.object({
  start: z.object({
    hour: z.number(),
    minute: z.number(),
  }),
  duration: z.object({
    hours: z.number(),
    minutes: z.number(),
  }),
});

/**
 * Default shift times - same as used in the old posting flow.
 * These are merged with API response to ensure we always have valid times.
 */
const DEFAULT_SHIFT_TIMES = {
  am: {
    start: { hour: 5, minute: 0 },
    duration: { hours: 8, minutes: 0 },
  },
  pm: {
    start: { hour: 13, minute: 0 },
    duration: { hours: 8, minutes: 0 },
  },
  noc: {
    start: { hour: 21, minute: 0 },
    duration: { hours: 8, minutes: 0 },
  },
  ori: {
    start: { hour: 5, minute: 0 },
    duration: { hours: 8, minutes: 0 },
  },
} as const;

/**
 * API response schema - matches the backend response format.
 * Contains standardDefs, extraDefs, and optional qualificationSpecific overrides.
 */
const extraDefinitionSchema = shiftTimeDefinitionSchema.extend({
  name: z.string(),
});

const getShiftTimesApiResponseSchema = z.object({
  standardDefs: z
    .object({
      am: shiftTimeDefinitionSchema.optional(),
      pm: shiftTimeDefinitionSchema.optional(),
      noc: shiftTimeDefinitionSchema.optional(),
    })
    .optional(),
  qualificationSpecific: z
    .record(z.string(), z.record(z.string(), shiftTimeDefinitionSchema))
    .optional(),
  extraDefs: z.array(extraDefinitionSchema).optional(),
});

const getShiftTimesQueryParamsSchema = z.object({
  qualification: z.string().optional(),
});

export type ShiftTimeDefinition = z.infer<typeof shiftTimeDefinitionSchema>;

export interface ShiftTimesResponse {
  am: ShiftTimeDefinition;
  pm: ShiftTimeDefinition;
  noc: ShiftTimeDefinition;
  ori: ShiftTimeDefinition;
}

export type QualificationSpecificOverrides = Record<
  string,
  Partial<Record<"am" | "pm" | "noc", ShiftTimeDefinition>>
>;

interface UseGetShiftTimesParams {
  workplaceId: string;
  qualification?: string;
}

interface UseGetShiftTimesOptions {
  enabled?: boolean;
}

function createShiftTimesUrl(workplaceId: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/shiftTimes/get/${workplaceId}`;
}

/**
 * Get the list of qualifications that have overrides for a specific time slot.
 * Used when "Apply to all" is selected to also update these qualification-specific overrides.
 */
export function getQualificationsWithSlotOverrides(
  qualificationSpecific: QualificationSpecificOverrides | undefined,
  slot: "am" | "pm" | "noc"
): string[] {
  if (!qualificationSpecific) {
    return [];
  }

  return Object.entries(qualificationSpecific)
    .filter(([, overrides]) => isDefined(overrides[slot]))
    .map(([qualification]) => qualification);
}

/**
 * Merges shift times with the standard priority:
 * DEFAULT_SHIFT_TIMES -> standardDefs -> qualificationSpecific
 */
export function mergeShiftTimes(
  standardDefs: Partial<ShiftTimesResponse> | undefined,
  qualificationSpecific: QualificationSpecificOverrides | undefined,
  qualification: string | undefined,
  extraDefs?: Array<ShiftTimeDefinition & { name: string }>
): ShiftTimesResponse {
  let qualificationSpecificEntries: Partial<ShiftTimesResponse> = {};
  if (
    isDefined(qualification) &&
    isDefined(qualificationSpecific) &&
    isDefined(qualificationSpecific[qualification])
  ) {
    qualificationSpecificEntries = qualificationSpecific[
      qualification
    ] as Partial<ShiftTimesResponse>;
  }

  const merged = {
    ...DEFAULT_SHIFT_TIMES,
    ...standardDefs,
    ...qualificationSpecificEntries,
  };

  const orientationExtra = extraDefs?.find((entry) => entry.name === "ori");
  return {
    ...merged,
    ori: orientationExtra
      ? { start: orientationExtra.start, duration: orientationExtra.duration }
      : merged.am,
  };
}

/**
 * Hook for fetching all shift times data for a facility.
 * Returns raw data (standardDefs + qualificationSpecific) without merging.
 * Use mergeShiftTimes() to apply qualification-specific overrides.
 *
 * Note: We use a single query key without qualification to ensure proper
 * cache invalidation across all qualifications.
 */
export function useGetShiftTimes(
  params: UseGetShiftTimesParams,
  options: UseGetShiftTimesOptions = {}
) {
  const { workplaceId, qualification } = params;
  const { enabled = true } = options;

  const url = createShiftTimesUrl(workplaceId);

  const query = useQuery({
    // Include qualification in query key so each qualification has its own cache entry
    queryKey: ["shiftTimes", workplaceId, qualification],
    queryFn: async () => {
      const response = await get({
        url,
        queryParamsSchema: getShiftTimesQueryParamsSchema,
        queryParams: isDefined(qualification) ? { qualification } : {},
        responseSchema: getShiftTimesApiResponseSchema,
      });

      return {
        standardDefs: response.data.standardDefs,
        qualificationSpecific: response.data.qualificationSpecific as
          | QualificationSpecificOverrides
          | undefined,
        extraDefs: response.data.extraDefs,
      };
    },
    enabled: enabled && Boolean(workplaceId),
  });

  // Memoize the merged data to prevent infinite re-renders.
  // Without this, mergeShiftTimes creates a new object on every render,
  // causing downstream useMemos and useEffects to re-run continuously.
  const mergedData = useMemo(() => {
    if (!query.data) {
      return undefined;
    }
    return mergeShiftTimes(
      query.data.standardDefs,
      query.data.qualificationSpecific,
      qualification,
      query.data.extraDefs
    );
  }, [query.data, qualification]);

  return {
    ...query,
    data: mergedData,
  };
}
